/* eslint-disable max-classes-per-file */
import React, { Component, useContext } from 'react'
import { connect } from 'react-redux'
import {
  LABEL,
  SECTION,
  ELLIPSE,
  SHAPE,
  IMAGE,
  WRAPPER,
  GROUP,
  LIST,
  INPUT,
  IMAGE_UPLOAD,
  FILE_UPLOAD,
  DATE_PICKER,
  CHECKBOX,
  FORM,
  SELECT,
  ROW,
  COLUMN,
  ROW_SPACER,
  COLUMN_SPACER,
  WEB_VIEW,
  COMPONENT_INSTANCE,
  LIBRARY_COMPONENT,
  LINE,
  LOCATION_INPUT,
  LAYOUT_SECTION,
  VIDEO,
  TABLE,
  SOCIAL_MEDIA_LIST,
} from '@adalo/constants'

import { getPushId } from 'utils/responsive'
import { getDeviceType } from 'utils/device'
import { getObjectBinding } from '../utils/dependencies'
import { getVisible, getVisibleOnDevice } from '../utils/visibility'
import { normalizeAttributes } from '../utils/styles'

import Label from './Label'
import Section from './Section'
import Line from './Line'
import Ellipse from './Ellipse'
import Shape from './Shape'
import Image from './Image'
import Video from './Video'
import Input from './Input'
import ImageUpload from './ImageUpload'
import FileUpload from './FileUpload'
import DatePicker from './DatePicker'
import Select from './Select'
import Checkbox from './Checkbox'
import Form from './Form'
import WebView from './WebView'
import Group from './Group'
import List, { WrappedListItem } from './List'
import Wrapper from './Wrapper'
import Row from './Row'
import Column from './Column'
import RowSpacer from './RowSpacer'
import ColumnSpacer from './ColumnSpacer'
import ComponentInstance from './ComponentInstance'
import LibraryComponent from './LibraryComponent'
import LocationInput from './LocationInput'
import LayoutSection from './LayoutSection'
import Table from './Table'

import { ResponsiveComponent } from './ResponsiveComponent'
import SocialMediaList from './SocialMediaList'
import AppContext from '../contexts/AppContext'
import { createBindingUtils, resolveBindingValues } from '../utils/bindingUtils'

const mapping = {
  [LABEL]: Label,
  [SECTION]: Section,
  [LINE]: Line,
  [IMAGE]: Image,
  [VIDEO]: Video,
  [ELLIPSE]: Ellipse,
  [SHAPE]: Shape,
  [GROUP]: Group,
  [WRAPPER]: Wrapper,
  [ROW]: Row,
  [COLUMN]: Column,
  [LIST]: List,
  [INPUT]: Input,
  [IMAGE_UPLOAD]: ImageUpload,
  [FILE_UPLOAD]: FileUpload,
  [DATE_PICKER]: DatePicker,
  [CHECKBOX]: Checkbox,
  [FORM]: Form,
  [SELECT]: Select,
  [ROW_SPACER]: RowSpacer,
  [COLUMN_SPACER]: ColumnSpacer,
  [WEB_VIEW]: WebView,
  [COMPONENT_INSTANCE]: ComponentInstance,
  [LIBRARY_COMPONENT]: LibraryComponent,
  [LOCATION_INPUT]: LocationInput,
  listItem: WrappedListItem,
  [LAYOUT_SECTION]: LayoutSection,
  [TABLE]: Table,
  [SOCIAL_MEDIA_LIST]: SocialMediaList,
}

export class ObjectRenderer extends Component {
  static contextType = AppContext

  /**
   *
   * @param {import('types').RunnerObject[]} children
   * @param {*} item
   * @param {import('types').LayoutMetadata} parentLayoutMetadata
   * @returns {React.JSX.Element[]}
   */
  renderChildren = (children, item, parentLayoutMetadata) => {
    const {
      active,
      component,
      topScreen,
      setScrollEnabled,
      scrollTo,
      object,
      listItemId,
      pushId: parentPushId,
      isResponsiveComponent,
      previewType,
      layoutType,
      setListItems,
    } = this.props

    if (children.filter(c => !c).length > 0) {
      console.log('--------> INFO:', object)
      throw new Error('ENCOUNTERED NULL CHILD!')
    }

    const itemId = item?.[object.id]?.id
    const listItemIdProp = listItemId || itemId

    return children.map(child => {
      const childId = child.id
      const pushId = getPushId(listItemId, childId, itemId, parentPushId)

      const { getBinding, getBindings, getBindingsList } =
        this.getBindingsFunc(pushId)

      return (
        <WrappedObjectRenderer
          key={pushId}
          pushId={pushId}
          object={child}
          component={component}
          active={active}
          topScreen={topScreen}
          setScrollEnabled={setScrollEnabled}
          isResponsiveComponent={isResponsiveComponent}
          scrollTo={scrollTo}
          listItemId={listItemIdProp}
          hasGroupParent={object.type === GROUP}
          previewType={previewType}
          layoutType={layoutType}
          parentLayoutMetadata={parentLayoutMetadata}
          getBinding={getBinding}
          getBindings={getBindings}
          getBindingsList={getBindingsList}
          setListItems={setListItems}
        />
      )
    })
  }

  /**
   *
   * @param {import('types').RunnerObject} object
   * @returns {React.JSX.Element | null}
   */
  getChild = object => {
    const { component, app, isResponsiveComponent, getFlags } = this.props

    if (isResponsiveComponent) {
      const {
        active,
        setHeight,
        pushId,
        visible,
        visibleOnDevice,
        setVisible,
        bindingData,
        adjustedNodeHeight,
        previewType,
        layoutType,
        parentLayoutMetadata,
      } = this.props
      const ObjectClass = this.getObjectClass(object)
      const branding = app && app.branding
      return (
        <ResponsiveComponent
          active={active}
          branding={branding}
          ObjectClass={ObjectClass}
          object={object}
          component={component}
          getBaseProps={this.getBaseProps}
          renderChildren={this.renderChildren}
          pushId={pushId}
          visible={visible}
          visibleOnDevice={visibleOnDevice}
          setVisible={setVisible}
          setHeight={setHeight}
          bindingData={bindingData}
          adjustedNodeHeight={adjustedNodeHeight}
          previewType={previewType}
          layoutType={layoutType}
          parentLayoutMetadata={parentLayoutMetadata}
          app={app}
          getFlags={getFlags}
        />
      )
    }

    return this.getNonResponsiveChild()
  }

  getBindingsFunc(pushId) {
    return resolveBindingValues(
      pushId,
      ['getBinding', 'getBindings', 'getBindingsList'],
      this.context,
      this.props
    )
  }

  getNonResponsiveChild = () => {
    const { object, visible } = this.props

    if (object.hidden || !visible) {
      return null
    }

    const ObjectClass = this.getObjectClass(object)
    const baseProps = this.getBaseProps(object)
    const { children } = object

    return (
      <ObjectClass {...baseProps}>
        {children ? this.renderChildren(children) : null}
      </ObjectClass>
    )
  }

  getObjectClass = object => {
    if (!mapping[object.type]) {
      console.warn(`I don't know how to render ${object.type}`)

      return null
    }

    if (object.type === SECTION && object.attributes?.purpose === 'container') {
      return mapping[LAYOUT_SECTION]
    }

    return mapping[object.type]
  }

  getBaseProps = (object, extraProps = {}) => {
    const {
      app,
      active,
      topScreen,
      component,
      bindingData,
      parentBindingData,
      isReusableComponent,
      getParams,
      getApp,
      getFileUploadsBaseURL,
      getImageUploadsBaseURL,
      onEnter,
      setUploading,
      formSubmitSuccess,
      setScrollEnabled,
      scrollTo,
      getFlags,
      isResponsiveComponent,
      listItemId,
      pushId,
      parentLayoutMetadata,
      previewType,
      setListItems,
    } = this.props

    const { getBinding, getBindings, getBindingsList } =
      this.getBindingsFunc(pushId)

    const baseProps = {
      isResponsiveComponent,
      isReusableComponent,
      app,
      component,
      object,
      active,
      bindingData,
      parentBindingData,
      topScreen,
      getBinding,
      getBindings,
      getBindingsList,
      getParams,
      getApp,
      renderChildren: this.renderChildren,
      getFileUploadsBaseURL,
      getImageUploadsBaseURL,
      onEnter,
      formSubmitSuccess,
      setScrollEnabled,
      scrollTo,
      listItemId,
      getFlags,
      pushId,
      parentLayoutMetadata,
      previewType,
      setListItems,
      ...extraProps,
    }

    if (object.type === IMAGE_UPLOAD || object.type === FILE_UPLOAD) {
      baseProps.setUploading = setUploading
    }

    return baseProps
  }

  render() {
    const { app, object } = this.props

    let normalizedObject = object
    if (app && app.branding) {
      normalizedObject = {
        ...object,
        attributes: normalizeAttributes(object.attributes, app.branding),
      }
    }

    return this.getChild(normalizedObject)
  }
}

// binding cache per screen id
const lastGoodBindingValues = {}

const mapStateToProps = (state, props) => {
  const {
    object,
    getBinding,
    getBindings,
    getBindingsList,
    getParams,
    getFlags,
    getFileUploadsBaseURL,
    getImageUploadsBaseURL,
    getApp,
    listItemId,
    pushId: listPushId,
    app,
    topScreen,
    setListItems,
  } = props

  const { magicLayout } = app || {}

  const pushId = listPushId || object.id

  // don't get binding data if screen is at the top of the stack
  // Getting binding data can be really expensive
  // Ideally, we'd have fast state selectors and memoization to make this fast
  // Check local cache instead
  const bindingData = !topScreen
    ? lastGoodBindingValues[object.id]
    : getObjectBinding(state, object, {
        getParams,
        getBinding,
        getBindingsList,
        getFileUploadsBaseURL,
        getImageUploadsBaseURL,
        getApp,
        listItemId,
      })

  if (topScreen) {
    lastGoodBindingValues[object.id] = bindingData
  }

  const deviceType = magicLayout ? getDeviceType(undefined, app) : null

  const visible = getVisible(
    state,
    object,
    {
      getParams,
      getBinding,
      getBindingsList,
      getApp,
      getFlags,
    },
    deviceType
  )

  const visibleOnDevice = magicLayout
    ? getVisibleOnDevice(object, deviceType, app)
    : true

  return {
    pushId,
    bindingData,
    visible,
    visibleOnDevice,
    app,
    getFlags,
    getBinding,
    getBindings,
    getBindingsList,
    setListItems,
  }
}

const ConnectedObjectRenderer = connect(mapStateToProps)(ObjectRenderer)

ConnectedObjectRenderer.whyDidYouRender = {
  logOnDifferentValues: true,
  customName: 'ConnectedObjectRenderer',
}

const WrappedObjectRenderer = props => {
  const context = useContext(AppContext)
  const { getApp } = context
  const { object } = props

  const { getBinding, getBindings, getBindingsList } = createBindingUtils(
    props,
    context
  )

  const app = getApp()

  return (
    <ConnectedObjectRenderer
      {...props}
      {...context}
      app={app}
      object={object}
      getBinding={getBinding}
      getBindings={getBindings}
      getBindingsList={getBindingsList}
    />
  )
}

WrappedObjectRenderer.whyDidYouRender = {
  logOnDifferentValues: true,
  customName: 'WrappedObjectRenderer',
}

export default WrappedObjectRenderer

export class MultipleObjectRenderer extends Component {
  render() {
    const { objects, ...props } = this.props

    return (
      <>
        {objects.map(obj => (
          <WrappedObjectRenderer {...props} key={obj.id} object={obj} />
        ))}
      </>
    )
  }
}
